import { EventEmitter } from 'events';
import { ReadonlyMat3, vec2 } from 'gl-matrix';
import {
    BrushOptions,
    DeformOptions,
    LineDeformOptions,
    LineOptions,
    SmoothBrushOptions,
} from '~/state/atoms/toolsAtoms';
import { Environment } from '../data/Environment';
import { setViewportCursorStyle } from '../data/common/customs/cursors/viewportCursorStyle';
import { ImageObject } from '../data/image/ImageObject';
import { RegionOfInterest } from '../data/structure/RegionOfInterest';
import type { EnvironmentViews } from '../view/EnvironmentViews';
import { PanZoom } from '../view/PanZoom';
import { TextureView } from '../view/TextureView';
import { ViewManager } from '../view/ViewManager';
import { PlaneView } from '../view/views/PlaneView';
import { DrawingConstraints } from './actions/DrawActionState';
import { ScrollAction } from './actions/ScrollAction';
import { ZoomAction } from './actions/ZoomAction';
import { GlobalCoordinator } from './coordinators/GlobalCoordinator';
import { GlobalPanCoordinator } from './coordinators/GlobalPanCoordinator';
import { GlobalRightButtonCoordinator } from './coordinators/GlobalRightButtonCoordinator';
import { EventHandlerResult } from './EventHandlerResult';
import { ModifierKeys } from './ModifierKeys';
import { CrosshairInteractionMode, Tool } from './Tool';
import { BoxZoomTool } from './tools/BoxZoomTool';
import { LineDrawTool } from './tools/contouring-tools/LineDrawTool';
import { DeformTool } from './tools/contouring-tools/DeformTool';
import { LineDeformTool } from './tools/contouring-tools/LineDeformTool';
import { SmoothBrushTool } from './tools/contouring-tools/SmoothBrushTool';
import {
    CrossSectionTool,
    CrossSectionToolCallbacks,
} from './tools/contouring-tools/cross-section/CrossSectionTool';
import {
    RectangleSelectTool,
    RectangleSelectToolOptions,
} from './tools/contouring-tools/RectangleSelectTool';
import { BrushTool } from './tools/contouring-tools/BrushTool';
import { TransformTool } from './tools/contouring-tools/TransformTool';
import { IntensityRegionTool } from './tools/IntensityRegionTool';
import { AnnotationPlacementTool } from './tools/AnnotationPlacementTool';
import { LineProfileTool } from './tools/LineProfileTool';
import { MeasurementTool } from './tools/MeasurementTool';
import { PanTool } from './tools/PanTool';
import { RegistrationMode, RegistrationTool } from './tools/RegistrationTool';
import { RotateViewTool } from './tools/RotateViewTool';
import {
    Measurement,
    MeasurementDraft,
    MeasurementKind,
} from '~/domain/measurement/measurement';
import { LineProfile, LineProfileDraft } from '~/domain/intensity/lineProfile';
import {
    IntensityRegion,
    IntensityRegionDraft,
    IntensityRegionShape,
} from '~/domain/intensity/intensityRegion';

// ───────── ToolConfig discriminated union ─────────

/** Shared fields required by every tool configuration. */
interface BaseToolConfig {
    panZoom: PanZoom;
    environment: Environment | undefined;
    /**
     * Views read live (of `environment`, or all): a tool paints on and follows
     * them as they come and go. Undefined arms a tool with no views to paint
     * on, whose cursors fall back to `document.body`.
     */
    views: EnvironmentViews | undefined;
}

interface PanToolConfig extends BaseToolConfig {
    tool: 'pan';
}

interface BrushToolConfig extends BaseToolConfig {
    tool: 'brush';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: BrushOptions;
    onBrushSizeChanged: (diameter: number) => void;
    constraints?: DrawingConstraints;
}

interface LineToolConfig extends BaseToolConfig {
    tool: 'line';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: LineOptions;
    constraints?: DrawingConstraints;
}

interface RegistrationToolConfig extends BaseToolConfig {
    tool: 'registration';
    mode: RegistrationMode | null;
}

interface BoxZoomToolConfig extends BaseToolConfig {
    tool: 'boxZoom';
}

/**
 * Combines the base fields every tool config needs (panZoom, views,
 * environment — unused by {@link RectangleSelectTool} itself, but required
 * so the config still fits the {@link ToolConfig} union) with the tool's
 * own options.
 */
interface RectangleSelectToolConfig
    extends BaseToolConfig,
        RectangleSelectToolOptions {}

/**
 * How the rotate tool reads and writes the oblique frame of the view it is
 * pressed on. The frame belongs to the pressed view's Environment, so both
 * calls name the pressed view.
 */
export interface FrameRotationAccess {
    /** The frame the pressed view shows, read once at press. */
    getFrameRotation: (view: PlaneView) => ReadonlyMat3;
    /** The frame a drag on the pressed view composed, reported per move. */
    onFrameRotationChanged: (rotation: ReadonlyMat3, view: PlaneView) => void;
}

interface RotateViewToolConfig extends BaseToolConfig, FrameRotationAccess {
    tool: 'rotateView';
}

interface TransformToolConfig extends BaseToolConfig {
    tool: 'transform';
    structure: RegionOfInterest | undefined;
    siblingSupports?: RegionOfInterest[];
}

interface LineDeformToolConfig extends BaseToolConfig {
    tool: 'lineDeform';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: LineDeformOptions;
    onSpreadChanged: (spreadPercent: number) => void;
}

interface DeformToolConfig extends BaseToolConfig {
    tool: 'deform';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: DeformOptions;
    onDiameterChanged: (diameter: number) => void;
}

interface SmoothBrushToolConfig extends BaseToolConfig {
    tool: 'smoothBrush';
    structure: RegionOfInterest | undefined;
    options: SmoothBrushOptions;
    onDiameterChanged: (diameter: number) => void;
}

interface CrossSectionToolConfig extends BaseToolConfig {
    tool: 'cross-section';
    structure: RegionOfInterest | undefined;
    callbacks: CrossSectionToolCallbacks;
}

interface MeasurementToolConfig extends BaseToolConfig {
    tool: 'measurement';
    mode: MeasurementKind;
    onDraftChanged: (draft: MeasurementDraft | null) => void;
    onCommit: (measurement: Measurement) => void;
}

interface LineProfileToolConfig extends BaseToolConfig {
    tool: 'lineProfile';
    onDraftChanged: (draft: LineProfileDraft | null) => void;
    onCommit: (profile: LineProfile) => void;
}

interface IntensityRegionToolConfig extends BaseToolConfig {
    tool: 'intensityRegion';
    shape: IntensityRegionShape;
    onDraftChanged: (draft: IntensityRegionDraft | null) => void;
    onCommit: (region: IntensityRegion) => void;
}

export { CrosshairInteractionMode } from './Tool';

/**
 * Discriminated union for {@link ToolManager.setActiveTool}.
 */
export type ToolConfig =
    | PanToolConfig
    | BrushToolConfig
    | LineToolConfig
    | RegistrationToolConfig
    | BoxZoomToolConfig
    | RectangleSelectToolConfig
    | RotateViewToolConfig
    | TransformToolConfig
    | LineDeformToolConfig
    | DeformToolConfig
    | SmoothBrushToolConfig
    | CrossSectionToolConfig
    | MeasurementToolConfig
    | LineProfileToolConfig
    | IntensityRegionToolConfig;
/**
 * {@link ToolManager} is responsible for handling the user actions.
 *
 * {@link HTMLCanvasElement Canvas} catches the action user performs in the form of an event.\
 * It then passes that event to {@link ToolManager}.
 *
 * {@link ToolManager} then propagates that event through the cycle of possible {@link EventHandler EventHandlers}.\
 * The event is propagated in the following order:\
 * {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} and
 * {@link ToolManager.activeView activeView}.
 *
 * {@link ToolManager} hands out the event to {@link EventHandler}.\
 * {@link EventHandler} inspects if it can handle the event and what to do with it.\
 * {@link EventHandler} then returns a {@link EventHandlerResult response} to {@link ToolManager} telling how to proceed:
 *
 * {@link EventHandler} may have started a new {@link ToolAction action}, in which case {@link ToolManager} should set that action as {@link ToolManager.activeAction active}.\
 * {@link EventHandler} may have ended an {@link ToolAction action}, in which case the {@link ToolManager}'s {@link ToolManager.activeAction active action} should be removed.\
 * {@link EventHandler} also informs whether the event was successfully handled or not,\
 * and whether the event should be propagated further or not.
 *
 * {@link ToolManager} then either proceeds the propagation further or exits the event handling.
 */
/**
 * Payload of the `stationaryRightClick` event: a right-click that never
 * became a window/level drag, and that the active tool did not claim for
 * its own action.
 *
 * The emitter is untyped, so this is exported for both ends to share one
 * shape rather than each asserting its own.
 */
export interface StationaryRightClickEvent {
    /** CLIENT coordinates of the PRESS — see onStationaryRightClick. */
    coordinates: vec2;
    /** The view pressed on, or undefined if it could not be resolved. */
    view: PlaneView | undefined;
}

/** The cursor over a view the active tool refuses to act through. */
const REFUSED_VIEW_CURSOR = 'not-allowed';

export class ToolManager {
    /**
     * Emits `toolStateChanged` after each change of the tool state, and
     * `stationaryRightClick` ({@link StationaryRightClickEvent}) for a
     * right-click the armed tool did not claim.
     */
    public readonly eventEmitter = new EventEmitter();

    /**
     * The currently active Tool.\
     * This is used in event propagation.
     */
    private activeTool?: Tool;

    /** The armed tool's views, read live; the cursor is written over them. */
    private views: EnvironmentViews | undefined;

    /** Stops following {@link views}; undefined while none is followed. */
    private unfollowViews: (() => void) | undefined;

    /**
     * The view under the pointer, or undefined once the pointer left the
     * viewports. It may show another Environment than the armed tool's, so
     * it is followed apart from {@link views}: a refused view of any
     * Environment shows the refusal while the pointer is over it.
     */
    private hoveredView: PlaneView | undefined;

    /** The cursor last written to each view that was written to. */
    private appliedCursors = new Map<PlaneView, string>();

    /** The cursor last written to document.body. */
    private appliedBodyCursor = '';

    /**
     * Coordinator for all global pan state and logic
     * (middle-button pan, key+drag pan, palm cursor).
     */
    private panCoordinator = new GlobalPanCoordinator();

    /**
     * Coordinator for the right mouse button: window/level drag,
     * tool-claimed clicks and stationary right-clicks.
     */
    private rightButtonCoordinator = new GlobalRightButtonCoordinator(() =>
        this.applyCursor()
    );

    /** All global coordinators, for uniform lifecycle dispatch. */
    private readonly coordinators: readonly GlobalCoordinator[] = [
        this.panCoordinator,
        this.rightButtonCoordinator,
    ];

    /**
     * Global {@link ZoomAction} bound to Ctrl+Wheel.
     * Tried AFTER the active tool so that custom wheel
     * behaviors (e.g. Shift+Wheel brush-size) take priority.
     */
    private globalZoomAction: ZoomAction | undefined;

    /**
     * Global {@link ScrollAction} for slice navigation.
     * Tried AFTER the active tool and global zoom so that
     * tool-specific and zoom wheel behaviors take priority.
     */
    private globalScrollAction: ScrollAction | undefined;

    /** The cursor of the crosshair overlay drag in progress, undefined
     *  when none. While set, canvas-level tool actions do not process
     *  mousemove events that leak through when the pointer drifts off
     *  the SVG line. */
    private crosshairDragCursor: string | undefined;

    /** Whether the active tool has been activated (onActivate called).
     *  Prevents redundant onActivate calls when onMouseEnter fires
     *  immediately after setActiveTool. */
    private _isToolActivated = false;

    /** External crosshair mode set by non-tool UI (e.g. POI move overlay).
     *  null means "no override — let tool-based logic decide".
     *
     *  TODO: Remove once POI Move is a proper {@link Tool}.  At that
     *  point its crosshair mode should be declared via the virtual
     *  method described in {@link getToolBasedCrosshairMode}, and
     *  this escape hatch (along with {@link setExternalCrosshairMode})
     *  can be deleted. */
    private _externalCrosshairMode: CrosshairInteractionMode | null = null;

    /**
     * Method to handle the EventHandlerResult.
     * @param result EventHandlerResult that should be handled
     * @returns true if the event should stop propagating, false to continue propagation further.
     */
    private handleEventResult(result: EventHandlerResult): boolean {
        if (result.invalidation) {
            // ToolManager holds no ViewManager reference: the static
            // ViewManager.invalidate routes this to the document's viewer.
            ViewManager.invalidate(
                result.invalidation.layers,
                result.invalidation.volume
            );
        }
        return result.stopPropagation;
    }

    /**
     * Returns true when a drawing action is active in a specific view
     * and the given view is a different view surface. Used to block pan in
     * viewports that are not the drawing viewport during an action.
     * A same-orientation view of another environment or another viewport
     * slot is a different surface ({@link PlaneView.isSameSurface}).
     */
    private isDrawingActiveInOtherView(view: TextureView): boolean {
        if (!(view instanceof PlaneView)) {
            return false;
        }
        const drawingView = this.activeTool?.getDrawingView();
        if (drawingView === undefined) {
            return false;
        }
        return !PlaneView.isSameSurface(view, drawingView);
    }

    /**
     * Create a {@link Tool} instance from a fully-typed
     * {@link ToolConfig}. Returns `null` when required data
     * (structure, image, transversal view) is missing.
     */
    private createToolForConfig = (config: ToolConfig): Tool | null => {
        switch (config.tool) {
            case 'pan':
                return new PanTool();
            case 'boxZoom':
                return new BoxZoomTool();
            case 'rectangle-select':
                return new RectangleSelectTool(config);
            case 'rotateView':
                return new RotateViewTool(
                    config.getFrameRotation,
                    config.onFrameRotationChanged
                );
            case 'brush': {
                const { structure, image, options, environment, constraints } =
                    config;
                if (!structure || !image) {
                    return null;
                }
                return new BrushTool(
                    options.mode,
                    image,
                    structure,
                    environment,
                    options.erase,
                    options.strokeMode,
                    options.diameter,
                    config.onBrushSizeChanged,
                    constraints
                );
            }

            case 'line': {
                const {
                    structure,
                    image,
                    options,
                    environment,
                    views,
                    constraints,
                } = config;
                if (!structure || !image) {
                    return null;
                }
                if (options.mode === 'draw' || options.mode === 'erase') {
                    return new LineDrawTool(
                        image,
                        structure,
                        options.mode,
                        environment,
                        views,
                        constraints
                    );
                }
                return null;
            }

            case 'registration':
                return new RegistrationTool(config.mode, config.environment);
            case 'transform': {
                const { structure, siblingSupports } = config;
                if (structure) {
                    return new TransformTool(
                        structure,
                        config.views,
                        config.environment,
                        siblingSupports
                    );
                }
                return null;
            }
            case 'lineDeform': {
                const { structure, image, options } = config;
                if (!structure || !image) {
                    return null;
                }
                return new LineDeformTool(
                    image,
                    structure,
                    config.environment,
                    options.spreadPercent,
                    config.onSpreadChanged
                );
            }
            case 'deform': {
                // A loaded image is a precondition of the WORKFLOW, not
                // a dependency of the tool: the deform stroke works on
                // the structure's own texture grid. The gate stays so
                // the tool cannot be entered from a state with no image
                // for the user to deform against.
                const { structure, image, options } = config;
                if (!structure || !image) {
                    return null;
                }
                return new DeformTool(
                    structure,
                    config.environment,
                    options.diameter,
                    options.mode,
                    config.onDiameterChanged
                );
            }
            case 'smoothBrush': {
                const { structure, options } = config;
                if (!structure) {
                    return null;
                }
                return new SmoothBrushTool(
                    structure,
                    config.environment,
                    options.diameter,
                    options.strength,
                    options.mode,
                    config.onDiameterChanged
                );
            }
            case 'cross-section': {
                const { structure } = config;
                if (!structure) {
                    return null;
                }
                return new CrossSectionTool(
                    structure,
                    config.views,
                    config.environment,
                    config.callbacks
                );
            }
            case 'measurement':
                return new MeasurementTool(
                    config.mode,
                    config.onDraftChanged,
                    config.onCommit
                );
            case 'lineProfile':
                return new LineProfileTool(
                    config.onDraftChanged,
                    config.onCommit
                );
            case 'intensityRegion':
                return new IntensityRegionTool(
                    config.shape,
                    config.onDraftChanged,
                    config.onCommit
                );
        }
    };

    /**
     * Set the active tool from a fully-typed {@link ToolConfig}.
     * Deactivates the current tool, creates the new one, and
     * (re-)initialises global coordinators and actions.
     */
    public setActiveTool(config: ToolConfig): void {
        this.withStateNotification(() => {
            const newActiveTool = this.createToolForConfig(config);
            // Always dispose the previous tool
            this.deactivateAndDisposeTool();
            if (newActiveTool) {
                this.activeTool = newActiveTool;
                this.followViews(config.views);
                this.panCoordinator.initialize(
                    config.panZoom,
                    config.tool === 'pan'
                );
                this.rightButtonCoordinator.initialize();
                // Re-emitted on the manager's own emitter rather than
                // handed a React setter, so the engine stays free of any
                // knowledge of what consumes it.
                this.rightButtonCoordinator.onStationaryRightClick = (
                    coordinates,
                    view
                ) => {
                    this.eventEmitter.emit('stationaryRightClick', {
                        coordinates,
                        view,
                    } satisfies StationaryRightClickEvent);
                };
                this.globalZoomAction = new ZoomAction(ModifierKeys.CTRL);
                this.globalScrollAction = new ScrollAction();
                this.activateActiveTool();
            } else {
                this.activeTool = undefined;
            }
        });
    }

    /**
     * Tear down everything this manager owns: deactivate and dispose
     * the active tool, end any pan or right-button gesture in progress
     * (a window/level drag keeps the window level it reached), and drop
     * the global wheel/scroll actions. Safe to call with no tool armed.
     */
    public dispose(): void {
        this.deactivateAndDisposeTool();
        this.activeTool = undefined;
        this.panCoordinator.onMouseLeave();
        this.rightButtonCoordinator.endGesture();
        this.globalZoomAction = undefined;
        this.globalScrollAction = undefined;
        this.followViews(undefined);
        this.hoveredView = undefined;
        this.applyCursor();
    }

    /**
     * Method to activate the active tool
     */
    private activateActiveTool(): void {
        if (this._isToolActivated) {
            return;
        }
        this._isToolActivated = true;
        const result = this.activeTool?.onActivate();
        if (result) {
            this.handleEventResult(result);
        }
        // Apply the tool's cursor on activation so a tool or mode change
        // is reflected immediately, without waiting for a mouse move.
        this.applyCursor();
    }

    /**
     * The cursor `view` should show: a crosshair drag's cursor, else
     * windowing hiding the pointer behind its overlay, else the pan palm,
     * else the not-allowed cursor over a view the active tool refuses, else
     * the active tool's cursor, else the default. The palm yields while a
     * stroke is in progress so it cannot appear mid-draw.
     */
    private resolveCursor(view: PlaneView): string {
        const isStroking = this.activeTool?.isStrokeInProgress() ?? false;
        const pan = isStroking ? undefined : this.panCoordinator.cursor;
        const refused =
            this.activeTool?.refusalFor(view) !== undefined
                ? REFUSED_VIEW_CURSOR
                : undefined;
        return (
            this.gestureCursor ??
            pan ??
            refused ??
            this.activeTool?.cursor ??
            'default'
        );
    }

    /**
     * The cursor of a gesture that keeps going outside the viewports, a
     * crosshair drag or the right button's window/level drag, or
     * undefined when none is running.
     */
    private get gestureCursor(): string | undefined {
        return this.crosshairDragCursor ?? this.rightButtonCoordinator.cursor;
    }

    /**
     * The cursor document.body should show. Only a gesture that keeps
     * going outside the viewports sets one, so its cursor holds when the
     * pointer drifts off the viewports but stays inside the window.
     */
    private resolveBodyCursor(): string {
        return this.gestureCursor ?? '';
    }

    /**
     * Follows `views` for the cursor: the handle followed before is let go,
     * and a view that joins or leaves repaints the cursor over the current
     * set, so a tile that mounts after the tool was armed shows it and one
     * that unmounts is forgotten.
     */
    private followViews(views: EnvironmentViews | undefined): void {
        this.unfollowViews?.();
        this.views = views;
        this.unfollowViews = views?.subscribe(() => this.applyCursor());
    }

    /**
     * Writes {@link resolveCursor} to each view of the armed tool and to the
     * view under the pointer when the tool refuses it, and
     * {@link resolveBodyCursor} to
     * document.body, each when it differs from what is shown. Views that
     * stop being targets are returned to the default first, so a cursor
     * never outlives the tool or the hover it was shown for. This is the
     * only place cursor styles are written, so what is shown always follows
     * from the current tool, coordinator, crosshair drag state, view set
     * and pointer.
     */
    private applyCursor(): void {
        const targets = new Map<PlaneView, string>();
        for (const view of this.views?.current ?? []) {
            targets.set(view, this.resolveCursor(view));
        }
        if (
            this.hoveredView &&
            !targets.has(this.hoveredView) &&
            this.activeTool?.refusalFor(this.hoveredView) !== undefined
        ) {
            targets.set(this.hoveredView, this.resolveCursor(this.hoveredView));
        }
        // A view carries the default until something else is written to
        // it, so the default is written only where another cursor was.
        for (const [view, applied] of this.appliedCursors) {
            if (!targets.has(view) && applied !== 'default') {
                setViewportCursorStyle([view], 'default');
            }
        }
        const applied = new Map<PlaneView, string>();
        for (const [view, cursor] of targets) {
            const shown = this.appliedCursors.get(view) ?? 'default';
            if (cursor !== shown) {
                setViewportCursorStyle([view], cursor);
            }
            applied.set(view, cursor);
        }
        this.appliedCursors = applied;
        const bodyCursor = this.resolveBodyCursor();
        if (bodyCursor !== this.appliedBodyCursor) {
            document.body.style.cursor = bodyCursor;
            this.appliedBodyCursor = bodyCursor;
        }
    }

    /**
     * Method to deactivate the active tool
     */
    private deactivateActiveTool(): void {
        this._isToolActivated = false;
        const result = this.activeTool?.onDeactivate();
        if (result) {
            this.handleEventResult(result);
        }
    }

    /**
     * Deactivate *and* dispose the current tool.  Called when the
     * tool instance is being permanently replaced or removed so
     * that long-lived resources (gizmos, etc.) are cleaned up.
     */
    private deactivateAndDisposeTool(): void {
        this.deactivateActiveTool();
        this.activeTool?.dispose();
    }

    /**
     * Method to get the active tool
     */
    public getActiveTool(): Tool | undefined {
        return this.activeTool;
    }

    /**
     * Discard any in-progress annotation placement (measurement, line
     * profile, intensity region) so a half-placed annotation cannot outlive the
     * action that abandoned it. A no-op for every other tool.
     */
    public cancelActivePlacementDraft(): void {
        const tool = this.getActiveTool();
        if (tool instanceof AnnotationPlacementTool) {
            tool.cancelDraft();
        }
    }

    /**
     * Ends the active tool's action bound to `view`, which is being removed
     * ({@link Tool.cancelActionIn}), and notifies the tool state so the views
     * that remain re-read what the action held off.
     */
    public cancelActiveToolActionIn(view: PlaneView): void {
        this.withStateNotification(() => {
            if (this.activeTool) {
                this.handleEventResult(this.activeTool.cancelActionIn(view));
            }
        });
    }

    /**
     * Whether the active tool has an action in progress (a line between
     * clicks, a placement draft, a drag). False with no active tool.
     */
    public isActiveToolActionInProgress(): boolean {
        return this.activeTool?.isActionInProgress() ?? false;
    }

    /** The view the active tool is currently drawing in, or undefined. */
    public getActiveToolDrawingView(): PlaneView | undefined {
        return this.activeTool?.getDrawingView();
    }

    /**
     * Returns the current {@link CrosshairInteractionMode} for the
     * Crosshairs component rendered in the given view.
     *
     * The result drives two independent decisions in the component:
     *   1. Which hit areas are interactive (line bodies, root handles,
     *      centre handle).
     *   2. Whether a visual "disabled" indicator is shown on the lines.
     *
     * The {@link Crosshairs} component subscribes to the
     * `toolStateChanged` event via {@link useCrosshairInteraction} and
     * updates hit-area `pointer-events` and visuals accordingly.
     *
     * @param view The PlaneView rendered by the Crosshairs component.
     */
    public getCrosshairInteractionMode(
        view: PlaneView | null
    ): CrosshairInteractionMode {
        const activeTool = this.activeTool;

        // Compute the mode driven solely by the active tool, ignoring
        // global pan/windowing state.  We need this first so that we can
        // preserve the visual when global actions are active.
        const toolMode = this.getToolBasedCrosshairMode(view, activeTool);

        // Non-tool UI override (e.g. POI move): apply when the tool
        // hasn't suppressed crosshairs itself.
        if (
            toolMode === CrosshairInteractionMode.Full &&
            this._externalCrosshairMode !== null
        ) {
            return this._externalCrosshairMode;
        }

        // Global pan or windowing active: disable interaction but keep
        // the visual stable so the crosshairs do not flash between
        // active and disabled appearances while the user is panning or
        // adjusting windowing.
        if (
            this.panCoordinator.isActive ||
            this.rightButtonCoordinator.isActive ||
            this.panCoordinator.isPanKeyHeld
        ) {
            const showsDisabledVisual =
                toolMode === CrosshairInteractionMode.RootsOnly ||
                toolMode === CrosshairInteractionMode.DrawingStroke;
            return showsDisabledVisual
                ? CrosshairInteractionMode.DrawingStroke
                : CrosshairInteractionMode.None;
        }

        return toolMode;
    }

    /**
     * Computes the crosshair interaction mode based purely on the
     * active tool state, without taking global pan / windowing into
     * account.  Delegates to {@link Tool.getCrosshairInteractionMode}
     * on the active tool — each tool owns its own policy.
     */
    private getToolBasedCrosshairMode(
        view: PlaneView | null,
        activeTool: Tool | undefined
    ): CrosshairInteractionMode {
        if (!activeTool) {
            return CrosshairInteractionMode.Full;
        }
        const isDrawingActiveInOtherView =
            view !== null && this.isDrawingActiveInOtherView(view);
        return activeTool.getCrosshairInteractionMode(
            isDrawingActiveInOtherView
        );
    }

    /**
     * Starts a crosshair overlay drag showing `cursor` until
     * {@link endCrosshairDrag}. The drag continues outside the
     * viewports, so the cursor is shown on document.body as well.
     */
    public beginCrosshairDrag(cursor: string): void {
        this.withCursorUpdate(() => {
            this.crosshairDragCursor = cursor;
        });
    }

    /** Ends the crosshair overlay drag, if one is in progress. */
    public endCrosshairDrag(): void {
        this.withCursorUpdate(() => {
            this.crosshairDragCursor = undefined;
        });
    }

    public setExternalCrosshairMode(
        mode: CrosshairInteractionMode | null
    ): void {
        this._externalCrosshairMode = mode;
        this.eventEmitter.emit('toolStateChanged');
    }

    /**
     * Wraps a state-changing operation so that the cursor is updated and
     * a generic `toolStateChanged` event is emitted after it completes.
     * Consumers (e.g. {@link useCrosshairInteraction}) subscribe
     * to this event and derive their own state; `useSyncExternalStore`
     * deduplicates so only actual changes cause re-renders.
     */
    private withStateNotification<T>(fn: () => T): T {
        try {
            return this.withCursorUpdate(fn);
        } finally {
            this.eventEmitter.emit('toolStateChanged');
        }
    }

    /**
     * Wraps an operation that may change what the tool or a coordinator
     * wants shown as the cursor, so the change is applied once it
     * completes. For the pointer events too frequent to notify state
     * consumers on.
     */
    private withCursorUpdate<T>(fn: () => T): T {
        try {
            return fn();
        } finally {
            this.applyCursor();
        }
    }

    /**
     * Mouse press event triggered by {@link ViewManager}.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link TextureView} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: TextureView
    ): boolean {
        return this.withStateNotification(() => {
            // Block all mouse presses while windowing is active
            if (this.rightButtonCoordinator.isActive) {
                return true;
            }

            // Global pan (key+drag or middle mouse button)
            if (
                !this.isDrawingActiveInOtherView(view) &&
                this.panCoordinator.onMousePress(
                    button,
                    modifierKeys,
                    coordinates,
                    view,
                    () => this.activeTool?.isStrokeInProgress() ?? false,
                    (result) => this.handleEventResult(result)
                )
            ) {
                return true;
            }

            // Global windowing (right-click-drag)
            if (
                view instanceof PlaneView &&
                this.rightButtonCoordinator.onMousePress(
                    button,
                    coordinates,
                    view,
                    modifierKeys,
                    () => this.activeTool?.isStrokeInProgress() ?? false,
                    this.panCoordinator.isActive,
                    (btn: number) =>
                        this.activeTool?.usesButtonDuringAction(btn) ?? false
                )
            ) {
                return true;
            }
            //if it fails,
            if (this.activeTool) {
                //let the active tool handle it
                if (
                    this.handleEventResult(
                        this.activeTool.onMousePress(
                            button,
                            modifierKeys,
                            coordinates,
                            view
                        )
                    )
                ) {
                    return true;
                }
            }
            //if that fails also,
            if (view && view.onMousePress) {
                //let the view try to handle it
                return this.handleEventResult(
                    view.onMousePress(button, modifierKeys, coordinates, view)
                );
            }
            //if none is able to handle the event, we just return
            //false to inform that the event couldn't be handled.
            return false;
        });
    }

    /**
     * Mouse move event triggered by {@link ViewManager}.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link TextureView} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public onMouseMove(
        coordinates: vec2,
        buttons: number,
        view: TextureView,
        movement?: vec2
    ): boolean {
        if (this.crosshairDragCursor !== undefined) {
            return false;
        }

        return this.withCursorUpdate(() => {
            this.hoveredView = view instanceof PlaneView ? view : undefined;

            // Global pan (key-pan resume + middle mouse button)
            if (
                !this.isDrawingActiveInOtherView(view) &&
                this.panCoordinator.onMouseMove(
                    coordinates,
                    buttons,
                    view,
                    () => this.activeTool?.isStrokeInProgress() ?? false,
                    (result) => this.handleEventResult(result)
                )
            ) {
                // Update active tool visual state during pan
                this.activeTool?.onViewChanged(coordinates, view);
                return true;
            }

            // Global windowing (right-click-drag)
            if (
                movement &&
                this.rightButtonCoordinator.onMouseMove(movement, buttons)
            ) {
                return true;
            }

            if (
                this.activeTool &&
                this.handleEventResult(
                    this.activeTool.onMouseMove(coordinates, buttons, view)
                )
            ) {
                return true;
            }
            if (view.onMouseMove) {
                return this.handleEventResult(
                    view.onMouseMove(coordinates, buttons, view)
                );
            }
            return false;
        });
    }

    /**
     * Mouse release event triggered by {@link ViewManager}.
     * @param button The button that triggered the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link TextureView} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public onMouseRelease(
        button: number,
        coordinates: vec2,
        view: TextureView
    ): boolean {
        return this.withStateNotification(() => {
            // Global pan (key+drag release or middle mouse button)
            if (
                this.panCoordinator.onMouseRelease(
                    button,
                    coordinates,
                    (result) => this.handleEventResult(result)
                )
            ) {
                return true;
            }

            // Global windowing (right-click-drag release)
            if (this.rightButtonCoordinator.onMouseRelease(button)) {
                return true;
            }

            if (
                this.activeTool &&
                this.handleEventResult(
                    this.activeTool.onMouseRelease(button, coordinates, view)
                )
            ) {
                return true;
            }
            if (view && view.onMouseRelease) {
                return this.handleEventResult(
                    view.onMouseRelease(button, coordinates)
                );
            }
            return false;
        });
    }

    /**
     * Mouse wheel event triggered by {@link ViewManager}.
     * @param delta The direction of the MouseWheel event.\
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link TextureView} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: PlaneView
    ): boolean {
        return this.withCursorUpdate(() =>
            this.dispatchMouseWheel(delta, modifierKeys, coordinates, view)
        );
    }

    private dispatchMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: PlaneView
    ): boolean {
        // Block all wheel events while the user is actively
        // drawing (mouse held). This prevents zoom, scroll,
        // and brush-size-change mid-stroke.
        const isStroking = this.activeTool?.isStrokeInProgress() ?? false;
        if (isStroking) {
            return true;
        }

        // 1. Active tool first (custom wheel behaviors like
        //    BrushTool's Shift+Wheel brush-size-change)
        if (this.activeTool) {
            if (
                this.handleEventResult(
                    this.activeTool.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                return true;
            }
        }
        // 2. Global zoom (Ctrl+Wheel)
        if (this.globalZoomAction) {
            if (
                this.handleEventResult(
                    this.globalZoomAction.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                this.panCoordinator.refreshPanAfterViewChange();
                // Update active tool to new zoom state
                if (this.activeTool) {
                    this.activeTool.onViewChanged(coordinates, view);
                }
                return true;
            }
        }
        // 3. Global scroll (slice navigation) — blocked during
        //    any active action (e.g. LineDrawTool between clicks)
        if (this.globalScrollAction && !this.isActiveToolActionInProgress()) {
            const result = this.globalScrollAction.onMouseWheel(
                delta,
                modifierKeys,
                coordinates,
                view
            );
            if (this.handleEventResult(result)) {
                this.panCoordinator.refreshPanAfterViewChange();
                // Update tool to new slice coordinates
                if (this.activeTool) {
                    this.activeTool.onViewChanged(coordinates, view);
                }
                return true;
            }
        }
        return false;
    }

    /**
     * Mouse double click event triggered by {@link ViewManager}.
     */
    public onDoubleClick(coordinates: vec2, view: PlaneView): boolean {
        return this.withStateNotification(() => {
            if (this.activeTool) {
                if (
                    this.handleEventResult(
                        this.activeTool.onMouseDoubleClick(coordinates, view)
                    )
                ) {
                    return true;
                }
            }
            return false;
        });
    }

    public onMouseLeave() {
        this.withStateNotification(() => {
            this.hoveredView = undefined;
            for (const coordinator of this.coordinators) {
                coordinator.onMouseLeave();
            }
            this.activeTool?.onMouseLeave();
            // Skip tool deactivation while a crosshair drag or a
            // tool action (e.g. transform drag) is in progress.
            // When the mouse moves fast and briefly exits the
            // viewport container, deactivateActiveTool() would
            // cancel the in-progress action and reset state.
            const isToolBusy = this.isActiveToolActionInProgress();
            if (this.crosshairDragCursor === undefined && !isToolBusy) {
                this.deactivateActiveTool();
            }
        });
    }

    public onMouseEnter() {
        this.activateActiveTool();
    }

    /**
     * Key press event triggered by {@link ViewManager}.
     * @param key The key that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param view The {@link TextureView} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or {@link view}.\
     * False otherwise.
     */
    public onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: TextureView
    ): boolean {
        return this.withStateNotification(() => {
            // Esc cancels active windowing (restores saved WW/WC)
            if (key === 'Escape' && this.rightButtonCoordinator.onEscKey()) {
                return true;
            }

            if (
                this.activeTool &&
                this.handleEventResult(
                    this.activeTool.onKeyPress(key, modifierKeys, view)
                )
            ) {
                return true;
            }
            // Global scroll (PageUp/Down, ArrowUp/Down). An action in
            // progress (e.g. drawing) holds the slice, but a slice key over
            // an image is still consumed: passed on, it would move the
            // keyboard focus to the next tile.
            if (this.globalScrollAction) {
                if (this.isActiveToolActionInProgress()) {
                    if (this.globalScrollAction.ownsKey(key, view)) {
                        return true;
                    }
                } else if (
                    this.handleEventResult(
                        this.globalScrollAction.onKeyPress(
                            key,
                            modifierKeys,
                            view
                        )
                    )
                ) {
                    this.panCoordinator.refreshPanAfterViewChange();
                    return true;
                }
            }
            if (view && view.onKeyPress) {
                if (
                    this.handleEventResult(view.onKeyPress(key, modifierKeys))
                ) {
                    return true;
                }
            }
            return false;
        });
    }

    /**
     * Sync Meta/Cmd key state from a mouse event.
     * Bridges the gap when Meta was pressed outside the browser
     * window and no keydown event fired.
     */
    public syncPanMetaKey(isMetaDown: boolean): void {
        this.panCoordinator.syncMetaKeyState(isMetaDown);
    }

    /**
     * Track pan-key press at the document level.
     * Called from ViewManager's document-level keydown listener
     * so that Space/Meta pan works regardless of which element
     * has DOM focus.
     * @returns true if the key was consumed (Space).
     */
    public onPanKeyDown(key: string): boolean {
        return this.withStateNotification(() =>
            this.panCoordinator.onPanKeyDown(key)
        );
    }

    /**
     * Track pan-key release at the document level.
     * Called from ViewManager's document-level keyup listener.
     * @returns true if the key was consumed.
     */
    public onPanKeyUp(key: string): boolean {
        return this.withStateNotification(() =>
            this.panCoordinator.onPanKeyUp(key)
        );
    }
}
